import React from 'react';
import { X, Trophy, MapPin, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { USER_TIERS, TIER_PRIVILEGES } from '../../constants';

interface TierGuideProps {
  onClose: () => void;
  currentTierLevel?: number;
}

const TierGuide: React.FC<TierGuideProps> = ({ onClose, currentTierLevel = 0 }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[10001] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-stone-950/90 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[var(--bg-base)] border border-[var(--border-primary)] rounded-modal shadow-3xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="p-8 border-b border-[var(--border-primary)] shrink-0 flex justify-between items-center bg-[var(--bg-input)]">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/10 rounded-2xl border border-amber-500/20 text-amber-500">
              <Trophy size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-[var(--text-primary)] tracking-tighter">{t('tierGuide.title')}</h2>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">{t('tierGuide.subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-[var(--bg-surface-alt)] rounded-xl">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-6 custom-scrollbar">

          {/* Intro */}
          <div className="bg-amber-500/5 border border-amber-500/20 p-5 rounded-panel space-y-3">
            <div className="flex items-center gap-3 text-amber-400">
              <MapPin size={20} className="shrink-0" />
              <h3 className="text-xs font-black uppercase tracking-widest">{t('tierGuide.howTitle')}</h3>
            </div>
            <p className="text-[var(--text-secondary)] text-xs leading-relaxed font-medium">
              {t('tierGuide.howDesc')}
            </p>
          </div>

          {/* Tier list */}
          <div className="space-y-3">
            {USER_TIERS.map((tier) => {
              const isEarned = currentTierLevel >= tier.level;
              const isCurrent = currentTierLevel === tier.level;
              const privilege = TIER_PRIVILEGES[tier.level];
              return (
                <div
                  key={tier.level}
                  className={`flex items-center gap-5 p-4 rounded-3xl border transition-all ${
                    isCurrent
                      ? 'bg-[var(--bg-surface)] border-white/20'
                      : isEarned
                        ? 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)]'
                        : 'bg-[var(--bg-surface-alt)] border-white/[0.03] opacity-60'
                  }`}
                >
                  {/* Diamond badge */}
                  <div className="shrink-0 flex flex-col items-center w-14">
                    <div className="relative">
                      <div
                        className="w-8 h-8 rotate-45 rounded-[4px] shadow-lg"
                        style={{
                          backgroundColor: isEarned ? tier.color : `${tier.color}44`,
                          boxShadow: isEarned ? `0 0 16px ${tier.color}44` : 'none'
                        }}
                      />
                      {isEarned && (
                        <CheckCircle2 size={12} className="absolute -top-1 -right-1 text-emerald-400" />
                      )}
                    </div>
                    <span className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mt-2">
                      Lvl {tier.level}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4
                        className="text-tiny font-black uppercase tracking-widest"
                        style={{ color: isEarned ? tier.color : `${tier.color}88` }}
                      >
                        {t(`tierGuide.tierNames.${tier.level}`, tier.name)}
                      </h4>
                      {isCurrent && (
                        <span className="text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md" style={{ backgroundColor: `${tier.color}22`, color: tier.color }}>
                          {t("auto.gamification.tierguide.du", "Du")}
                        </span>
                      )}
                    </div>
                    <p className="text-2xs text-[var(--text-muted)] font-bold">
                      {t('tierGuide.threshold', { count: tier.threshold })}
                    </p>
                    <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">
                      {t(`tierGuide.levels.${tier.level}`)}
                    </p>
                    {privilege && (
                      <p className="text-2xs font-bold mt-1" style={{ color: isEarned ? tier.color : `${tier.color}66` }}>
                        ★ {t(`tierGuide.privileges.${tier.level}`, privilege.descriptionNo)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--border-primary)] bg-[var(--bg-input)]">
          <button onClick={onClose} className="w-full bg-stone-100 text-stone-950 py-4 rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all">
            {t('tierGuide.button')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TierGuide;
